import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { currentLocaleTag } from '../lib/formatting'
import {
  AlertCircle,
  Edit2,
  CheckCircle2,
  Filter,
  Loader2,
  Plus,
  Search,
  Wallet,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { confirmDialog } from '../lib/dialogs'
import { SELECTOR_PAGE_SIZE } from '../lib/constants'
import Modal from '../components/ui/Modal'
import { useAuth } from '../contexts/AuthContext'
import { formatCents, parseEurosToCents, centsToEurosInput } from '../lib/money'
import { useServiceLines } from '../lib/service-lines'
import { useFirmName } from '../lib/firm-name'
import {
  type CreateExpenseInput,
  type ExpenseCategoryT,
  type ExpenseDto,
  type ExpenseListResponse,
  type ExpenseStatusT,
  type OpportunityListResponse,
} from '@toga/shared'
import { useExpenseCategories } from '../lib/expense-categories'

/**
 * Colores de la SITUACIÓN de tesorería. El ámbar marca lo que exige una acción
 * (sin pagar, o dinero que la empresa debe a alguien); el verde, lo cerrado.
 */
const TREASURY_PILL: Record<string, string> = {
  SIN_PAGAR: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
  PARCIAL: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
  ADELANTADO_PENDIENTE: 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/40',
  ADELANTADO_REEMBOLSADO: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
  PAGADO_JR2_SIN_CUADRAR: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/40',
  PAGADO_JR2: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
  ANULADO: 'bg-slate-500/15 t-secondary border-slate-500/30',
}

const STATUS_PILL: Record<ExpenseStatusT, string> = {
  PENDIENTE: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40',
  PAGADO: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
  ANULADO: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
}

const CURRENT_YEAR = new Date().getFullYear()
const YEAR_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2]

export default function Expenses() {
  const { t } = useTranslation('expenses')
  const auth = useAuth()
  const canWrite =
    auth.status === 'authenticated' &&
    (auth.user.role === 'ADMIN' || auth.user.role === 'CONSULTOR')

  const [items, setItems] = useState<ExpenseDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [yearFilter, setYearFilter] = useState<number | ''>(CURRENT_YEAR)
  const [statusFilter, setStatusFilter] = useState<ExpenseStatusT | ''>('')
  const [q, setQ] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [openExpense, setOpenExpense] = useState<ExpenseDto | null>(null)
  const [editExpense, setEditExpense] = useState<ExpenseDto | null>(null)
  /** Filtro por quién puso el dinero: '' todos, 'company' la empresa, o un userId. */
  const [payerFilter, setPayerFilter] = useState('')
  const [payerSummary, setPayerSummary] = useState<ExpenseListResponse['payerSummary']>([])
  // "JR2" en vez de "La empresa": sale de Ajustes → Compañía, no de un literal,
  // para que la demo y cualquier otra instalación digan su propio nombre.
  const firmName = useFirmName()
  const companyLabel = firmName ?? t('detail.paidByCompany')

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (yearFilter !== '') params.set('year', String(yearFilter))
      if (statusFilter) params.set('status', statusFilter)
      if (q.trim()) params.set('q', q.trim())
      if (payerFilter) params.set('paidBy', payerFilter)
      params.set('pageSize', '100')
      const data = await api.get<ExpenseListResponse>(`/expenses?${params.toString()}`)
      setItems(data.items)
      setPayerSummary(data.payerSummary ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [yearFilter, statusFilter, q, payerFilter, t])

  useEffect(() => {
    void reload()
  }, [reload])

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <Wallet size={20} className="text-amber-700 dark:text-amber-300" />
            {t('title')}
          </h1>
          <p className="text-xs t-muted">
            {t('subtitle')}
          </p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-amber-600 via-amber-500 to-amber-700 hover:shadow-glow-amber flex items-center gap-1.5"
          >
            <Plus size={14} /> {t('newButton')}
          </button>
        )}
      </header>

      {/* Filtros */}
      <div className="surface p-3 flex items-end gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={14}
            className="absolute left-2 top-1/2 -translate-y-1/2 t-faint pointer-events-none"
          />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('filters.searchPlaceholder')}
            className="w-full bg-transparent border border-base rounded-lg pl-7 pr-3 py-1.5 text-sm t-primary placeholder:t-faint focus:outline-none focus:border-amber-500/50"
          />
        </div>
        <label className="text-xs t-muted flex items-center gap-2">
          <Filter size={12} className="t-faint" /> {t('filters.year')}
          <select
            value={yearFilter}
            onChange={(e) =>
              setYearFilter(e.target.value === '' ? '' : Number(e.target.value))
            }
            className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-amber-500/50"
          >
            <option value="">{t('filters.all')}</option>
            {YEAR_OPTIONS.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs t-muted flex items-center gap-2">
          {t('filters.status')}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ExpenseStatusT | '')}
            className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-amber-500/50"
          >
            <option value="">{t('filters.all')}</option>
            <option value="PENDIENTE">{t('enums:ExpenseStatus.PENDIENTE')}</option>
            <option value="PAGADO">{t('enums:ExpenseStatus.PAGADO')}</option>
            <option value="ANULADO">{t('enums:ExpenseStatus.ANULADO')}</option>
          </select>
        </label>
      </div>

      {/* Quién ha puesto qué. Cada ficha filtra la lista: es la forma rápida de
          ver "todo lo que he adelantado yo" antes de pedir el reembolso. */}
      {payerSummary.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs t-muted">{t('filters.paidBy')}</span>
          <button
            type="button"
            onClick={() => setPayerFilter('')}
            className={[
              'px-2 py-1 rounded-lg text-xs border transition',
              payerFilter === '' ? 'border-amber-500/60 bg-amber-500/10 t-primary' : 'border-base t-secondary hover:bg-hover',
            ].join(' ')}
          >
            {t('filters.all')}
          </button>
          {payerSummary.map((p) => {
            const key = p.userId ?? 'company'
            const active = payerFilter === key
            return (
              <button
                key={key}
                type="button"
                onClick={() => setPayerFilter(active ? '' : key)}
                className={[
                  'px-2 py-1 rounded-lg text-xs border transition flex items-center gap-1.5',
                  active ? 'border-amber-500/60 bg-amber-500/10 t-primary' : 'border-base t-secondary hover:bg-hover',
                ].join(' ')}
              >
                <span>{p.userName ?? companyLabel}</span>
                {/* Manda lo PENDIENTE de devolver: es lo único que dice qué hay
                    que hacer. Lo adelantado en total va detrás, apagado. */}
                <span className="font-mono">
                  {p.userId ? formatCents(p.pendingCents) : formatCents(p.amountCents)}
                </span>
                {p.userId != null && p.pendingCents !== p.amountCents && (
                  <span className="t-faint">
                    {t('filters.ofAdvanced', { total: formatCents(p.amountCents) })}
                  </span>
                )}
                <span className="t-faint">({p.count})</span>
              </button>
            )
          })}
        </div>
      )}

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div className="surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-input-soft)] text-xs uppercase tracking-wider t-faint">
            <tr>
              <th className="px-3 py-2 text-left">{t('table.number')}</th>
              <th className="px-3 py-2 text-left">{t('table.date')}</th>
              <th className="px-3 py-2 text-left">{t('table.supplier')}</th>
              <th className="px-3 py-2 text-left">{t('table.concept')}</th>
              <th className="px-3 py-2 text-left">{t('table.paidBy')}</th>
              <th className="px-3 py-2 text-right">{t('table.subtotal')}</th>
              <th className="px-3 py-2 text-right">{t('table.vat')}</th>
              <th className="px-3 py-2 text-right">{t('table.irpf')}</th>
              <th className="px-3 py-2 text-right">{t('table.total')}</th>
              <th className="px-3 py-2 text-left">{t('table.treasury')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center t-muted">
                  <Loader2 size={14} className="inline-block animate-spin" /> {t('loading')}
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-10 text-center">
                  <Wallet size={28} className="mx-auto t-faint mb-2" />
                  <p className="t-muted text-sm">
                    {q || statusFilter
                      ? t('empty.filtered')
                      : t('empty.none')}
                  </p>
                </td>
              </tr>
            )}
            {items.map((r) => (
              <tr
                key={r.id}
                className={[
                  'border-t border-base/40 hover:bg-hover cursor-pointer',
                  // Un gasto anulado sigue en la lista a propósito (el registro
                  // de facturas recibidas no debe tener huecos), pero no puede
                  // parecer uno vivo: se apaga entero y se tacha el importe.
                  r.status === 'ANULADO'
                    ? 'opacity-60 bg-[var(--bg-input-soft)] [&_td]:line-through [&_td:last-child]:no-underline'
                    : '',
                ].join(' ')}
                onClick={() => setOpenExpense(r)}
              >
                <td className="px-3 py-2 t-secondary font-mono text-xs">
                  {r.number}/{r.year}
                </td>
                <td className="px-3 py-2 t-secondary text-xs">
                  {new Date(r.expenseDate).toLocaleDateString(currentLocaleTag())}
                </td>
                <td className="px-3 py-2 t-primary font-medium">{r.supplierName}</td>
                <td className="px-3 py-2 t-secondary text-xs max-w-xs truncate">
                  {r.concept}
                </td>
                <td className="px-3 py-2 text-xs t-secondary whitespace-nowrap">
                  {r.payers.length === 0
                    ? r.paidCents > 0
                      ? companyLabel
                      : '—'
                    : r.payers.map((p) => p.userName ?? companyLabel).join(' + ')}
                </td>
                <td className="px-3 py-2 text-right font-mono t-secondary">
                  {formatCents(r.subtotalCents)}
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs t-faint">
                  {formatCents(r.vatCents)}
                </td>
                {/* La retención NO se le paga al proveedor: se ingresa a Hacienda
                    con el modelo 111. Se marca en ámbar para que se vea que resta. */}
                <td className="px-3 py-2 text-right font-mono text-xs">
                  {r.irpfCents > 0 ? (
                    <span className="text-amber-700 dark:text-amber-300">
                      −{formatCents(r.irpfCents)}
                      {r.irpfRate != null && (
                        <span className="t-faint ml-1">({(r.irpfRate * 100).toFixed(0)}%)</span>
                      )}
                    </span>
                  ) : (
                    <span className="t-faint">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono t-primary font-semibold">
                  {formatCents(r.totalCents)}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-flex px-1.5 py-0.5 rounded text-[10px] border whitespace-nowrap ${TREASURY_PILL[r.treasuryStatus] ?? STATUS_PILL[r.status]}`}
                    title={
                      r.pendingReimbursementCents > 0
                        ? t('treasury.pendingTooltip', {
                            amount: formatCents(r.pendingReimbursementCents),
                          })
                        : undefined
                    }
                  >
                    {r.treasuryStatus === 'ADELANTADO_PENDIENTE'
                      ? t('treasury.ADELANTADO_PENDIENTE', {
                          who: r.payers
                            .filter((p) => p.userId && p.amountCents > p.reimbursedCents)
                            .map((p) => (p.userName ?? '').split(' ')[0])
                            .join(' + '),
                        })
                      : r.treasuryStatus === 'ADELANTADO_REEMBOLSADO'
                        ? t('treasury.ADELANTADO_REEMBOLSADO')
                        : r.treasuryStatus === 'PAGADO_JR2' ||
                            r.treasuryStatus === 'PAGADO_JR2_SIN_CUADRAR'
                          ? t(`treasury.${r.treasuryStatus}`, { company: companyLabel })
                          : t(`treasury.${r.treasuryStatus}`)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <ExpenseFormModal
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false)
            void reload()
          }}
        />
      )}

      {editExpense && (
        <ExpenseFormModal
          initial={editExpense}
          onClose={() => setEditExpense(null)}
          onSaved={() => {
            setEditExpense(null)
            void reload()
          }}
        />
      )}

      {openExpense && (
        <ExpenseDetailModal
          expense={openExpense}
          canWrite={canWrite}
          onClose={() => setOpenExpense(null)}
          onChanged={() => {
            setOpenExpense(null)
            void reload()
          }}
          onEdit={() => {
            setEditExpense(openExpense)
            setOpenExpense(null)
          }}
        />
      )}
    </div>
  )
}

// ---------- Crear ----------

interface CreateProps {
  /**
   * Gasto a EDITAR. Sin él, alta. Solo se editan los PENDIENTE: en cuanto hay
   * pago, el asiento y la deuda con el proveedor ya están en la contabilidad y
   * el servidor lo rechaza (para esos está "Anular gasto").
   */
  initial?: ExpenseDto
  onClose: () => void
  onSaved: () => void
}

interface OpportunityOption {
  id: string
  code: string
  title: string
}

function ExpenseFormModal({ initial, onClose, onSaved }: CreateProps) {
  const { t } = useTranslation('expenses')
  const { lines } = useServiceLines()
  const { categories, defaultCode } = useExpenseCategories()
  const [form, setForm] = useState({
    supplierName: initial?.supplierName ?? '',
    supplierNif: initial?.supplierNif ?? '',
    concept: initial?.concept ?? '',
    category: (initial?.category ?? '') as ExpenseCategoryT,
    expenseDate: (initial?.expenseDate ?? new Date().toISOString()).slice(0, 10),
    opportunityId: initial?.opportunityId ?? '',
    serviceLine: initial?.serviceLine ?? '',
  })
  /**
   * Desglose del IVA. Arranca con UNA fila —el caso normal— y se añaden más
   * cuando el papel mezcla tipos: el ticket del súper trae 10 % en la comida y
   * 21 % en el refresco, y meterlo con un tipo medio deduce de más.
   */
  const [vatRows, setVatRows] = useState<{ baseEuros: string; vatPct: string }[]>(() =>
    initial && initial.vatLines.length > 0
      ? initial.vatLines.map((l) => ({
          baseEuros: centsToEurosInput(l.subtotalCents),
          vatPct: String(Number((l.vatRate * 100).toFixed(2))),
        }))
      : [{ baseEuros: '', vatPct: '21' }]
  )
  const [opportunities, setOpportunities] = useState<OpportunityOption[]>([])
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Preselecciona la tipología por defecto del catálogo al cargarlo (si el
  // usuario aún no ha elegido otra).
  useEffect(() => {
    if (categories.length > 0) {
      setForm((f) => (f.category ? f : { ...f, category: defaultCode }))
    }
  }, [categories.length, defaultCode])

  useEffect(() => {
    api
      .get<OpportunityListResponse>(`/opportunities?pageSize=${SELECTOR_PAGE_SIZE}`)
      .then((res) => {
        setOpportunities(
          res.items.map((o) => ({ id: o.id, code: o.code, title: o.title }))
        )
      })
      .catch(() => {
        // Silencioso: el selector queda vacío y el usuario puede crear sin oportunidad.
      })
  }, [])

  const upd = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const pctToRate = (raw: string) => {
    const n = Number(raw.replace(',', '.'))
    return Number.isFinite(n) && n >= 0 && n <= 100 ? n / 100 : 0
  }
  const vatLines = vatRows.map((r) => {
    const base = parseEurosToCents(r.baseEuros)
    const rate = pctToRate(r.vatPct)
    return { vatRate: rate, subtotalCents: base, vatCents: Math.round(base * rate) }
  })
  const subtotalCents = vatLines.reduce((a, l) => a + l.subtotalCents, 0)
  const vatCents = vatLines.reduce((a, l) => a + l.vatCents, 0)
  const totalCents = subtotalCents + vatCents
  const vatRate = vatLines.length === 1 ? (vatLines[0]?.vatRate ?? 0) : 0

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (subtotalCents <= 0) {
      return setError(t('errors.invalidSubtotal'))
    }
    setSubmitting(true)
    try {
      const payload: CreateExpenseInput = {
        supplierName: form.supplierName.trim(),
        supplierNif: form.supplierNif.trim() || null,
        concept: form.concept.trim(),
        category: form.category,
        expenseDate: new Date(form.expenseDate).toISOString(),
        subtotalCents,
        vatRate,
        // Se manda siempre: el servidor deriva los totales de aquí y así el
        // desglose queda guardado también cuando solo hay un tipo.
        vatLines,
        opportunityId: form.opportunityId || null,
        serviceLine: form.serviceLine || null,
      }
      if (initial) await api.patch(`/expenses/${initial.id}`, payload)
      else await api.post('/expenses', payload)
      onSaved()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? t('errors.saveFailed'))
      } else {
        setError(t('errors.network'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-amber-500/40 focus:border-amber-500/60'

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={initial ? t('form.editTitle', { number: `${initial.year}/${initial.number}` }) : t('form.modalTitle')}
      maxWidth="max-w-2xl"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('form.supplier')}</label>
            <input
              required
              value={form.supplierName}
              onChange={(e) => upd('supplierName', e.target.value)}
              placeholder={t('form.supplierPlaceholder')}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('form.nif')}</label>
            <input
              value={form.supplierNif}
              onChange={(e) => upd('supplierNif', e.target.value)}
              placeholder="B12345678"
              className={inputCls}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs t-muted mb-1.5">{t('form.concept')}</label>
          <input
            required
            value={form.concept}
            onChange={(e) => upd('concept', e.target.value)}
            placeholder={t('form.conceptPlaceholder')}
            className={inputCls}
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('form.category')}</label>
            <select
              value={form.category}
              onChange={(e) => upd('category', e.target.value as ExpenseCategoryT)}
              className={inputCls}
            >
              {categories.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('form.date')}</label>
            <input
              type="date"
              required
              value={form.expenseDate}
              onChange={(e) => upd('expenseDate', e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1.5">{t('form.serviceLine')}</label>
            <select
              value={form.serviceLine}
              onChange={(e) => upd('serviceLine', e.target.value)}
              className={inputCls}
            >
              <option value="">{t('form.unassigned')}</option>
              {lines.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_5rem_1fr_2rem] gap-2 text-xs t-muted">
            <span>{t('form.subtotal')}</span>
            <span className="text-right">{t('form.vat')}</span>
            <span className="text-right">{t('form.vatAmount')}</span>
            <span />
          </div>
          {vatRows.map((row, i) => (
            <div key={i} className="grid grid-cols-[1fr_5rem_1fr_2rem] gap-2 items-center">
              <input
                required
                inputMode="decimal"
                value={row.baseEuros}
                onChange={(e) =>
                  setVatRows((rs) => rs.map((r, j) => (j === i ? { ...r, baseEuros: e.target.value } : r)))
                }
                placeholder="100,00"
                className={`${inputCls} font-mono text-right`}
              />
              <input
                required
                inputMode="decimal"
                value={row.vatPct}
                onChange={(e) =>
                  setVatRows((rs) => rs.map((r, j) => (j === i ? { ...r, vatPct: e.target.value } : r)))
                }
                placeholder="21"
                className={`${inputCls} font-mono text-right`}
              />
              <div className={`${inputCls} font-mono text-right t-secondary bg-[var(--bg-input-soft)]`}>
                {formatCents(vatLines[i]?.vatCents ?? 0)}
              </div>
              {vatRows.length > 1 ? (
                <button
                  type="button"
                  onClick={() => setVatRows((rs) => rs.filter((_, j) => j !== i))}
                  className="t-faint hover:text-rose-500 text-sm"
                  aria-label={t('form.removeVatRate')}
                >
                  ×
                </button>
              ) : (
                <span />
              )}
            </div>
          ))}
          <div className="flex items-center justify-between gap-3 pt-1">
            <button
              type="button"
              onClick={() => setVatRows((rs) => [...rs, { baseEuros: '', vatPct: '10' }])}
              className="text-xs text-toga-600 dark:text-toga-300 hover:underline"
            >
              + {t('form.addVatRate')}
            </button>
            <div className="flex items-baseline gap-2">
              <span className="text-xs t-muted">{t('form.total')}</span>
              <span className="font-mono text-sm t-primary">{formatCents(totalCents)}</span>
            </div>
          </div>
          {vatRows.length > 1 && (
            <p className="text-[11px] t-faint">{t('form.vatRatesHint')}</p>
          )}
        </div>

        <div>
          <label className="block text-xs t-muted mb-1.5">{t('form.opportunity')}</label>
          <select
            value={form.opportunityId}
            onChange={(e) => upd('opportunityId', e.target.value)}
            className={inputCls}
          >
            <option value="">{t('form.noOpportunity')}</option>
            {opportunities.map((o) => (
              <option key={o.id} value={o.id}>
                {o.code} · {o.title}
              </option>
            ))}
          </select>
          <p className="text-[11px] t-faint mt-1">
            {t('form.opportunityHint')}
          </p>
        </div>

        {error && (
          <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)]"
          >
            {t('actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting || !form.category}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-amber-600 via-amber-500 to-amber-700 hover:shadow-glow-amber disabled:opacity-50 flex items-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {t('actions.create')}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// ---------- Detalle ----------

interface DetailProps {
  /** Abre el formulario de edición. Lo gestiona la página para no anidar modales. */
  onEdit: () => void
  expense: ExpenseDto
  canWrite: boolean
  onClose: () => void
  onChanged: () => void
}

function ExpenseDetailModal({ expense, canWrite, onClose, onChanged, onEdit }: DetailProps) {
  const { t } = useTranslation('expenses')
  const { nameOf } = useServiceLines()
  const { nameOf: categoryNameOf } = useExpenseCategories()
  const [paying, setPaying] = useState(false)
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10))
  /**
   * Quién puso el dinero. Vacío = la empresa desde su banco. Con una persona, el
   * asiento abona su cuenta corriente de socio (551) en vez del banco: al
   * proveedor ya se le pagó, y lo que queda vivo es la deuda con quien adelantó
   * el dinero, hasta que se le reembolse por transferencia.
   */
  const [split, setSplit] = useState<{ userId: string; euros: string }[]>([
    { userId: '', euros: '' },
  ])
  const [people, setPeople] = useState<{ id: string; fullName: string }[]>([])
  /** Corrección del pagador de un gasto YA pagado (panel aparte del de pagar). */
  const [reassignOpen, setReassignOpen] = useState(false)
  const [reassigning, setReassigning] = useState(false)
  /**
   * Edición en línea del concepto. Es el único campo que se puede corregir con
   * el gasto ya pagado: describe, no contabiliza. Los que entran por OCR nacen
   * con "Factura E262… — Proveedor", que no dice nada dentro de seis meses.
   */
  const [conceptDraft, setConceptDraft] = useState<string | null>(null)
  const [savingConcept, setSavingConcept] = useState(false)
  const firmName = useFirmName()
  const companyLabel = firmName ?? t('detail.paidByCompany')

  useEffect(() => {
    if (!paying && !reassignOpen) return
    // OJO: este endpoint devuelve { users }, NO { items } como casi todos los
    // demás listados. Leerlo mal dejaba el desplegable con solo "La empresa".
    api
      .get<{ users: { id: string; fullName: string; isActive: boolean }[] }>('/users')
      .then((r) => setPeople((r.users ?? []).filter((u) => u.isActive)))
      .catch(() => setPeople([]))
  }, [paying, reassignOpen])

  // Lo que se va a pagar: total menos la retención (esa va a Hacienda vía 111)
  // menos lo ya pagado. Es el importe contra el que tiene que cuadrar el reparto.
  const payableCents = reassignOpen
    ? expense.paidCents
    : expense.totalCents - expense.irpfCents - expense.paidCents
  const splitCents = split.map((r) => parseEurosToCents(r.euros))
  const splitSum = splitCents.reduce((a, c) => a + c, 0)
  // Con UNA sola fila no se pide importe: se entiende que paga todo. Con varias
  // hay que repartir, y la suma debe cuadrar al céntimo o el servidor lo rechaza.
  const splitOk = split.length === 1 || splitSum === payableCents

  /** Reparte a partes iguales y le da el céntimo suelto a la primera fila. */
  const splitEqually = () => {
    const n = split.length
    const each = Math.floor(payableCents / n)
    setSplit((rows) =>
      rows.map((r, i) => ({
        ...r,
        euros: centsToEurosInput(i === 0 ? payableCents - each * (n - 1) : each),
      }))
    )
  }
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  // F43 — Borrado con avisos: paso de confirmación + opción de llevarse el justificante.
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [alsoDeleteDocument, setAlsoDeleteDocument] = useState(true)
  const [deleting, setDeleting] = useState(false)
  const [voiding, setVoiding] = useState(false)

  const onPay = async () => {
    setError(null)
    setSubmitting(true)
    try {
      // Sin amountCents: el servidor liquida el pendiente real (total − IRPF −
      // ya pagado). La retención IRPF se paga a Hacienda vía modelo 111, no al
      // proveedor, así que enviar totalCents descuadraría la 400 (L3 #35/#55).
      await api.post(`/expenses/${expense.id}/pay`, {
        paymentDate: new Date(payDate).toISOString(),
        // Con una sola fila y la empresa como pagador no se manda nada: es el
        // comportamiento de siempre y no ensucia el asiento con una pierna.
        ...(split.length === 1 && !split[0]?.userId
          ? {}
          : {
              payers: split.map((r, i) => ({
                userId: r.userId || null,
                amountCents: split.length === 1 ? payableCents : (splitCents[i] ?? 0),
              })),
            }),
      })
      onChanged()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? t('errors.payFailed'))
      } else {
        setError(t('errors.network'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * Anular. A diferencia de borrar, vale con el gasto ya PAGADO y el registro
   * se queda a la vista marcado como ANULADO en vez de desaparecer — en un
   * libro de facturas recibidas, un hueco en la numeración es peor que una
   * anulación visible. El servidor reversa todos sus asientos.
   */
  /**
   * Corregir de quién salió el dinero en un gasto YA pagado. El servidor reversa
   * el asiento de pago y lo vuelve a postear con las contrapartidas nuevas, así
   * que el dato y la contabilidad no pueden divergir.
   */
  const onReassign = async () => {
    setError(null)
    setReassigning(true)
    try {
      await api.post(`/expenses/${expense.id}/reassign-payers`, {
        payers: split.map((r, i) => ({
          userId: r.userId || null,
          amountCents: split.length === 1 ? expense.paidCents : (splitCents[i] ?? 0),
        })),
      })
      onChanged()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? t('detail.reassignFailed'))
      } else setError(t('errors.network'))
    } finally {
      setReassigning(false)
    }
  }

  const saveConcept = async () => {
    const value = (conceptDraft ?? '').trim()
    if (!value || value === expense.concept) return setConceptDraft(null)
    setError(null)
    setSavingConcept(true)
    try {
      // Se manda SOLO el concepto: el servidor admite este campo en cualquier
      // estado, pero rechaza el resto si el gasto ya no está pendiente.
      await api.patch(`/expenses/${expense.id}`, { concept: value })
      setConceptDraft(null)
      onChanged()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? t('errors.saveFailed'))
      } else setError(t('errors.network'))
    } finally {
      setSavingConcept(false)
    }
  }

  const onVoid = async () => {
    if (
      !(await confirmDialog({
        message: t('void.confirm', { number: `${expense.year}/${expense.number}` }),
        tone: 'danger',
      }))
    )
      return
    setError(null)
    setVoiding(true)
    try {
      await api.post(`/expenses/${expense.id}/void`, {})
      onChanged()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? t('void.failed'))
      } else {
        setError(t('errors.network'))
      }
    } finally {
      setVoiding(false)
    }
  }

  const onDelete = async () => {
    setError(null)
    setDeleting(true)
    try {
      const qs =
        expense.documentId && alsoDeleteDocument ? '?deleteDocument=true' : ''
      await api.delete(`/expenses/${expense.id}${qs}`)
      onChanged()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? t('delete.failed'))
      } else {
        setError(t('errors.network'))
      }
    } finally {
      setDeleting(false)
    }
  }

  /**
   * Editor del reparto. Se usa tanto al marcar pagado como al corregir de quién
   * salió el dinero en un gasto ya pagado: es la misma decisión, y tenerlo dos
   * veces en el árbol era la forma segura de que uno de los dos se quedara atrás.
   */
  const splitEditor = (
              <div className="w-full space-y-1.5">
                <span className="text-xs t-muted">{t('detail.paidBy')}</span>
                {split.map((row, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <select
                      value={row.userId}
                      onChange={(e) =>
                        setSplit((rs) =>
                          rs.map((r, j) => (j === i ? { ...r, userId: e.target.value } : r))
                        )
                      }
                      className="flex-1 px-3 py-1.5 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500/40"
                    >
                      <option value="">{companyLabel}</option>
                      {people.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.fullName}
                        </option>
                      ))}
                    </select>
                    {split.length > 1 && (
                      <>
                        <input
                          inputMode="decimal"
                          value={row.euros}
                          onChange={(e) =>
                            setSplit((rs) =>
                              rs.map((r, j) => (j === i ? { ...r, euros: e.target.value } : r))
                            )
                          }
                          placeholder="0,00"
                          className="w-28 px-3 py-1.5 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary text-sm font-mono text-right focus:outline-none focus:ring-1 focus:ring-emerald-500/40"
                        />
                        <button
                          type="button"
                          onClick={() => setSplit((rs) => rs.filter((_, j) => j !== i))}
                          className="t-faint hover:text-rose-500 text-sm px-1"
                          aria-label={t('detail.removePayer')}
                        >
                          ×
                        </button>
                      </>
                    )}
                  </div>
                ))}
                <div className="flex items-center gap-3 text-[11px]">
                  <button
                    type="button"
                    onClick={() =>
                      setSplit((rs) => {
                        // Se reparte solo al añadir: el caso normal es a medias,
                        // y dejar las filas a cero obliga a teclear dos importes
                        // para lo que casi siempre es una división exacta.
                        const n = rs.length + 1
                        const each = Math.floor(payableCents / n)
                        return [...rs, { userId: '', euros: '' }].map((r, i) => ({
                          ...r,
                          euros: centsToEurosInput(
                            i === 0 ? payableCents - each * (n - 1) : each
                          ),
                        }))
                      })
                    }
                    className="text-toga-600 dark:text-toga-300 hover:underline"
                  >
                    + {t('detail.addPayer')}
                  </button>
                  {split.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={splitEqually}
                        className="text-toga-600 dark:text-toga-300 hover:underline"
                      >
                        {t('detail.splitEqually')}
                      </button>
                      <span className={splitOk ? 't-faint' : 'text-rose-500'}>
                        {formatCents(splitSum)} / {formatCents(payableCents)}
                      </span>
                    </>
                  )}
                </div>
              </div>
  )

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={t('detail.title', { number: expense.number, year: expense.year })}
      maxWidth="max-w-xl"
    >
      <div className="space-y-3 text-sm">
        <Row label={t('form.supplier')} value={expense.supplierName} />
        {expense.supplierNif && <Row label={t('detail.nifLabel')} value={expense.supplierNif} />}
        {conceptDraft === null ? (
          <Row
            label={t('form.concept')}
            value={
              <span className="inline-flex items-center gap-2 justify-end">
                <span>{expense.concept}</span>
                {canWrite && expense.status !== 'ANULADO' && (
                  <button
                    type="button"
                    onClick={() => setConceptDraft(expense.concept)}
                    className="t-faint hover:text-toga-500 shrink-0"
                    aria-label={t('actions.edit')}
                    title={t('detail.editConcept')}
                  >
                    <Edit2 size={13} />
                  </button>
                )}
              </span>
            }
          />
        ) : (
          <div className="py-1.5 space-y-1.5">
            <span className="text-xs t-muted">{t('form.concept')}</span>
            <textarea
              autoFocus
              rows={2}
              value={conceptDraft}
              onChange={(e) => setConceptDraft(e.target.value)}
              className="w-full px-3 py-1.5 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary text-sm focus:outline-none focus:ring-1 focus:ring-toga-500/40"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConceptDraft(null)}
                className="px-2.5 py-1 rounded-lg text-xs t-secondary border border-base hover:bg-hover"
              >
                {t('actions.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void saveConcept()}
                disabled={savingConcept || !conceptDraft.trim()}
                className="px-2.5 py-1 rounded-lg text-xs font-semibold text-white bg-toga-600 hover:bg-toga-500 disabled:opacity-50 flex items-center gap-1.5"
              >
                {savingConcept && <Loader2 size={12} className="animate-spin" />}
                {t('actions.save')}
              </button>
            </div>
          </div>
        )}
        <Row label={t('form.category')} value={categoryNameOf(expense.category)} />
        <Row
          label={t('form.date')}
          value={new Date(expense.expenseDate).toLocaleDateString(currentLocaleTag())}
        />
        {expense.opportunityCode && (
          <Row label={t('detail.opportunityLabel')} value={expense.opportunityCode} />
        )}
        {expense.serviceLine && (
          <Row
            label={t('form.serviceLine')}
            value={nameOf(expense.serviceLine)}
          />
        )}
        <Row label={t('form.subtotal')} value={formatCents(expense.subtotalCents)} mono />
        {/* Con varios tipos, el "IVA (21 %)" de abajo sería un tipo medio que no
            existe en ningún sitio: se enseña el desglose real del papel. */}
        {expense.vatLines.length > 1 &&
          expense.vatLines.map((l, i) => (
            <Row
              key={i}
              label={t('detail.vatWithRate', { rate: (l.vatRate * 100).toFixed(0) })}
              value={`${formatCents(l.subtotalCents)} → ${formatCents(l.vatCents)}`}
              mono
            />
          ))}
        <Row
          label={
            expense.vatLines.length > 1
              ? t('detail.vatTotal')
              : t('detail.vatWithRate', { rate: (expense.vatRate * 100).toFixed(0) })
          }
          value={formatCents(expense.vatCents)}
          mono
        />
        <Row label={t('form.total')} value={formatCents(expense.totalCents)} mono highlight />
        <Row
          label={t('filters.status')}
          value={
            <span
              className={`inline-flex px-1.5 py-0.5 rounded text-[10px] border ${STATUS_PILL[expense.status]}`}
            >
              {t('enums:ExpenseStatus.' + expense.status)}
            </span>
          }
        />
        {expense.payers.map((p, i) => (
          <Row
            key={i}
            label={i === 0 ? t('detail.paidBy') : ''}
            value={`${p.userName ?? companyLabel} · ${formatCents(p.amountCents)}`}
            highlight={!!p.userId}
          />
        ))}
        {expense.paidAt && (
          <Row
            label={t('detail.paidOn')}
            value={new Date(expense.paidAt).toLocaleDateString(currentLocaleTag())}
          />
        )}
      </div>

      {error && (
        <div className="mt-3 text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      {canWrite && expense.status === 'PENDIENTE' && confirmingDelete && (
        <div className="mt-4 border border-rose-500/40 bg-rose-500/5 rounded-lg p-4 space-y-3">
          <p className="text-sm font-semibold text-rose-700 dark:text-rose-300">
            {t('delete.warningTitle')}
          </p>
          <ul className="text-xs t-secondary space-y-1 list-disc pl-4">
            <li>{t('delete.warnEntry')}</li>
            <li>{t('delete.warnIrreversible')}</li>
          </ul>
          {expense.documentId && (
            <label className="flex items-center gap-2 text-xs t-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={alsoDeleteDocument}
                onChange={(e) => setAlsoDeleteDocument(e.target.checked)}
                className="rounded accent-rose-500"
              />
              {t('delete.alsoDocument')}
            </label>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirmingDelete(false)}
              disabled={deleting}
              className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover disabled:opacity-50"
            >
              {t('actions.cancel')}
            </button>
            <button
              type="button"
              onClick={() => void onDelete()}
              disabled={deleting}
              className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-rose-600 to-rose-500 hover:shadow-lg disabled:opacity-50 flex items-center gap-1.5"
            >
              {deleting && <Loader2 size={14} className="animate-spin" />}
              {t('delete.confirm')}
            </button>
          </div>
        </div>
      )}

      {canWrite && expense.status === 'PENDIENTE' && !confirmingDelete && !paying && (
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={onEdit}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover flex items-center gap-1.5"
          >
            <Edit2 size={14} /> {t('actions.edit')}
          </button>
        </div>
      )}

      {canWrite && expense.status !== 'PENDIENTE' && expense.status !== 'ANULADO' && (
        <div className="mt-4 border-t border-base/40 pt-3 flex justify-between items-center gap-2 flex-wrap">
          <p className="text-xs t-faint max-w-md">{t('void.hint')}</p>
          {/* Un kilometraje o una dieta no tienen pagador que reasignar: se le
              deben a la persona desde el propio devengo (6XX contra 551), sin
              pasar por la 400. El servidor lo rechaza con un 409; aquí se oculta
              para no ofrecer una operación imposible. */}
          {expense.kind === 'FACTURA_RECIBIDA' && (
          <button
            type="button"
            onClick={() => {
              // Se precarga lo que ya hubiera para no obligar a reescribirlo.
              setSplit(
                expense.payers.length > 0
                  ? expense.payers.map((p) => ({
                      userId: p.userId ?? '',
                      euros: centsToEurosInput(p.amountCents),
                    }))
                  : [{ userId: '', euros: centsToEurosInput(expense.paidCents) }]
              )
              setReassignOpen((v) => !v)
            }}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            {t('detail.reassignPayers')}
          </button>
          )}
          <button
            type="button"
            onClick={() => void onVoid()}
            disabled={voiding}
            className="px-3 py-1.5 rounded-lg text-sm text-rose-500 dark:text-rose-400 border border-rose-500/40 hover:bg-rose-500/10 disabled:opacity-50 flex items-center gap-1.5"
          >
            {voiding && <Loader2 size={14} className="animate-spin" />}
            {t('void.button')}
          </button>
        </div>
      )}

      {reassignOpen && (
        <div className="mt-3 border border-base/60 rounded-lg p-4 space-y-3">
          <p className="text-xs t-muted">{t('detail.reassignHint')}</p>
          {splitEditor}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setReassignOpen(false)}
              className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
            >
              {t('actions.cancel')}
            </button>
            <button
              type="button"
              onClick={() => void onReassign()}
              disabled={reassigning || !splitOk}
              className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-emerald-700 hover:shadow-glow-emerald disabled:opacity-50 flex items-center gap-1.5"
            >
              {reassigning && <Loader2 size={14} className="animate-spin" />}
              {t('detail.reassignConfirm')}
            </button>
          </div>
        </div>
      )}

      {canWrite && expense.status === 'PENDIENTE' && !confirmingDelete && (
        <div className="mt-4 border-t border-base/40 pt-3">
          {!paying ? (
            <div className="flex justify-between items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setConfirmingDelete(true)}
                className="px-3 py-1.5 rounded-lg text-sm text-rose-500 dark:text-rose-400 border border-rose-500/40 hover:bg-rose-500/10 flex items-center gap-1.5"
              >
                {t('delete.button')}
              </button>
              <button
                type="button"
                onClick={() => setPaying(true)}
                className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 via-emerald-500 to-emerald-700 hover:shadow-glow-emerald flex items-center gap-1.5"
              >
                <CheckCircle2 size={14} /> {t('actions.markPaid')}
              </button>
            </div>
          ) : (
            <div className="flex items-end gap-3 flex-wrap">
              <label className="text-xs t-muted">
                {t('detail.paymentDate')}
                <input
                  type="date"
                  value={payDate}
                  onChange={(e) => setPayDate(e.target.value)}
                  className="block mt-1 w-full px-3 py-1.5 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] t-primary focus:outline-none focus:ring-1 focus:ring-emerald-500/40 focus:border-emerald-500/60"
                />
              </label>
              {splitEditor}
              <button
                type="button"
                onClick={() => void onPay()}
                disabled={submitting || !splitOk}
                className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 via-emerald-500 to-emerald-700 hover:shadow-glow-emerald disabled:opacity-50 flex items-center gap-1.5"
              >
                {submitting && <Loader2 size={14} className="animate-spin" />}
                {t('actions.confirmPayment', { amount: formatCents(expense.totalCents) })}
              </button>
              <button
                type="button"
                onClick={() => setPaying(false)}
                className="px-3 py-1.5 rounded-lg text-sm bg-[var(--bg-hover)] hover:bg-[var(--bg-input-soft)] t-secondary border border-[var(--border)]"
              >
                {t('actions.cancel')}
              </button>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}

function Row({
  label,
  value,
  mono,
  highlight,
}: {
  label: string
  value: React.ReactNode
  mono?: boolean
  highlight?: boolean
}) {
  return (
    <div className="flex justify-between items-center">
      <span className="t-muted text-xs">{label}</span>
      <span
        className={[
          mono ? 'font-mono' : '',
          highlight ? 't-primary font-semibold' : 't-secondary',
        ].join(' ')}
      >
        {value}
      </span>
    </div>
  )
}
